import { useCallback, useEffect } from "react";
import { ThicknessClass, CreateThicknessClass, UpdateThicknessClass } from "../types/models/ThicknessClass.d";
import { UpdateEntityThicknessClass } from "../types/models/ThicknessClass";
import { useGetThicknessClassListQuery, useInsertThicknessClassMutation, useUpdateThicknessClassMutation, useDeleteThicknessClassMutation, } from "../apis/ThicknessClass.api";
import { useDeleteDialog, useDescriptionAlert } from '../../share';
import { useNavigate } from "react-router";
import { t } from "@lingui/core/macro";

export default function useThicknessClass(page: number, pageSize: number) {

    const navigate = useNavigate()
    const { openDialog, closeDialog } = useDeleteDialog();

    const { openSuccessAlert, openFailAlert } = useDescriptionAlert()

    const [insertThicknessClassMutation, { 
        isLoading: insertThicknessClassLoading, 
        isSuccess: insertThicknessClassIsSuccess,
        isError: insertThicknessClassIsError,
        error: insertThicknessClassError,
    }] = useInsertThicknessClassMutation()
    const [updateThicknessClassMutation, { 
        isLoading: updateThicknessClassLoading,
        isSuccess: updateThicknessClassIsSuccess,
        isError: updateThicknessClassIsError,
        error: updateThicknessClassError,
    }] = useUpdateThicknessClassMutation()
    const [deleteThicknessClassMutation, { 
        isLoading: deleteThicknessClassLoading,
        isSuccess: deleteThicknessClassIsSuccess,
        isError: deleteThicknessClassIsError,
        error: deleteThicknessClassError,
    }] = useDeleteThicknessClassMutation()

    const loading =
        insertThicknessClassLoading ||
        updateThicknessClassLoading ||
        deleteThicknessClassLoading ||
        false

    const insertThicknessClass = useCallback(async (thicknessClass: CreateThicknessClass, redirectPath: string|undefined) => {
        try {
            const data = await insertThicknessClassMutation(thicknessClass).unwrap()
            if (data) {
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate(`../${data.id}`, {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("add error", e)
            openFailAlert(t`Add error: ` + e.toString())
        }
    }, [insertThicknessClassMutation, navigate])
    
    useEffect(() => {
        if (insertThicknessClassIsSuccess) {
            openSuccessAlert('Thickness Class created')
        } else if (insertThicknessClassIsError) {
            console.log("add error", insertThicknessClassError)
            openFailAlert(t`Thickness Class add error: ` + insertThicknessClassError?.data?.message)
        }
    }, [
        insertThicknessClassIsSuccess,
        insertThicknessClassIsError,
        insertThicknessClassError
    ]);

    const updateThicknessClass = useCallback(async (thicknessClass: UpdateEntityThicknessClass, redirectPath: string|undefined) => {
        try {
            const data = await updateThicknessClassMutation(thicknessClass).unwrap();
            if (data != null) {
                //if update success
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate('..', {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("update error", e)
            openFailAlert(t`Update error: ` + e.toString())
        }
    }, [updateThicknessClassMutation, navigate])
    
    useEffect(() => {
        if (updateThicknessClassIsSuccess) {
            openSuccessAlert(t`Thickness Class updated`)
        } else if (updateThicknessClassIsError) {
            console.log("update error", updateThicknessClassError)
            openFailAlert(t`Thickness Class update error: ` + updateThicknessClassError?.data?.message)
        }
    }, [
        updateThicknessClassIsSuccess,
        updateThicknessClassIsError,
        updateThicknessClassError
    ]);

    const deleteThicknessClass = useCallback((thicknessClass: ThicknessClass, redirectPath: string|undefined) => {
        openDialog(async () => {
            thicknessClass = {
                ...thicknessClass,
            }
            try {
                const { data } = await deleteThicknessClassMutation(thicknessClass);
                if (data != null) {
                    if (redirectPath) {
                        navigate(redirectPath, {relative: 'path'})
                    }
                }
            } catch (e) {
                console.log("delete error", e)
                openFailAlert(t`Delete error: ` + e.toString())
            }
            closeDialog()
        });
    }, [deleteThicknessClassMutation, closeDialog, openDialog])
    
    useEffect(() => {
        if (deleteThicknessClassIsSuccess) {
            openSuccessAlert(t`Thickness Class deleted`)
        } else if (deleteThicknessClassIsError) {
            console.log("delete error", deleteThicknessClassError)
            openFailAlert(t`Thickness Class delete error: ` + deleteThicknessClassError?.data?.message)
        }
    }, [
        deleteThicknessClassIsSuccess,
        deleteThicknessClassIsError,
        deleteThicknessClassError
    ]);

    return {
        loading,
        insertThicknessClass,
        updateThicknessClass,
        deleteThicknessClass
    }
}
